import { doc, getDoc, setDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from './firebase';
import { perfTracker } from './perfLogger';

export interface SiteSettings {
  siteTitle: string;
  siteLogoHeaderUrl: string;
  siteLogoFooterUrl: string;
  siteLogoAuthUrl?: string;
  siteLogoEmailUrl?: string;
  siteTaglineHeader: string;
  siteTaglineFooter: string;
  siteFaviconUrl: string;
  newsletterEnabled?: boolean;
  newsletterBadgeText?: string;
  newsletterHeading?: string;
  newsletterDescription?: string;
  newsletterButtonText?: string;
  newsletterSuccessDiscountCode?: string;
  newsletterDiscountPercentage?: string;
  footerDescription?: string;
  footerCopyright?: string;
  socialInstagramUrl?: string;
  socialWhatsappUrl?: string;
  socialTelegramUrl?: string;
  socialDribbbleUrl?: string;
}

export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  siteTitle: 'RH Editography',
  siteLogoHeaderUrl: '/assets/logo.svg',
  siteLogoFooterUrl: '/assets/logo.svg',
  siteLogoAuthUrl: '/assets/logo.svg',
  siteLogoEmailUrl: '/assets/logo.svg',
  siteTaglineHeader: 'Digital Template Marketplace',
  siteTaglineFooter: '3D Digital Vector Marketplace',
  siteFaviconUrl: '/assets/logo.svg',
  newsletterEnabled: true,
  newsletterBadgeText: 'Join 35,000+ Designers & Professionals',
  newsletterHeading: 'Get 20% Off Your First 3D Master Template Download',
  newsletterDescription: 'Subscribe to the RH Editography VIP briefing for weekly 300 DPI vector releases, security microprint templates, and instant discount passes.',
  newsletterButtonText: 'Get VIP Pass',
  newsletterSuccessDiscountCode: 'RH3D20',
  newsletterDiscountPercentage: '20%',
  footerDescription: 'World-class digital marketplace specialized in editable vector document templates, biometric passports, ID cards, diplomas, financial statements, and corporate agreements.',
  footerCopyright: '© 2026 RH Editography. All Rights Reserved.',
  socialInstagramUrl: '',
  socialWhatsappUrl: '',
  socialTelegramUrl: '',
  socialDribbbleUrl: '',
};

const COLLECTION_NAME = 'site_settings';
const DOC_ID = 'general';
const PATH_KEY = `${COLLECTION_NAME}/${DOC_ID}`;

let cachedSiteSettings: SiteSettings = { ...DEFAULT_SITE_SETTINGS };

try {
  const saved = localStorage.getItem('rh_site_settings');
  if (saved) {
    cachedSiteSettings = { ...DEFAULT_SITE_SETTINGS, ...JSON.parse(saved) };
  }
} catch (e) {}

export function getLocalSiteSettings(): SiteSettings {
  return cachedSiteSettings;
}

// Multiplexing subscriber Set for site settings
const siteSettingsSubscribers = new Set<(settings: SiteSettings) => void>();
let activeSiteSettingsUnsub: Unsubscribe | null = null;

export async function fetchSiteSettingsFromFirestore(): Promise<SiteSettings> {
  const startTime = performance.now();
  try {
    const docRef = doc(db, COLLECTION_NAME, DOC_ID);
    const snap = await getDoc(docRef);
    const duration = performance.now() - startTime;
    perfTracker.logFetch(PATH_KEY, duration, snap.metadata.fromCache ? 'cache' : 'server', 1);

    if (snap.exists()) {
      const data = snap.data();
      const merged: SiteSettings = { ...DEFAULT_SITE_SETTINGS, ...data };
      cachedSiteSettings = merged;
      try {
        localStorage.setItem('rh_site_settings', JSON.stringify(merged));
      } catch (e) {}
      applySiteSettingsToDOM(merged);
      return merged;
    }
  } catch (err) {
    console.warn('Error fetching Site Settings from Firestore:', err);
  }
  return cachedSiteSettings;
}

export function subscribeToSiteSettingsFromFirestore(
  callback: (settings: SiteSettings) => void
): () => void {
  // Immediately pass cached data for 0ms render
  callback(cachedSiteSettings);
  applySiteSettingsToDOM(cachedSiteSettings);

  siteSettingsSubscribers.add(callback);

  if (!activeSiteSettingsUnsub) {
    const docRef = doc(db, COLLECTION_NAME, DOC_ID);
    activeSiteSettingsUnsub = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const merged: SiteSettings = { ...DEFAULT_SITE_SETTINGS, ...data };
        cachedSiteSettings = merged;
        try {
          localStorage.setItem('rh_site_settings', JSON.stringify(merged));
        } catch (e) {}
        applySiteSettingsToDOM(merged);
        siteSettingsSubscribers.forEach((cb) => {
          try { cb(merged); } catch (e) {}
        });
      }
    }, (err) => {
      console.warn('Real-time site settings fetch error:', err);
    });
  }

  return () => {
    siteSettingsSubscribers.delete(callback);
    if (siteSettingsSubscribers.size === 0 && activeSiteSettingsUnsub) {
      activeSiteSettingsUnsub();
      activeSiteSettingsUnsub = null;
    }
  };
}

export async function saveSiteSettingsToFirestore(settings: SiteSettings): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, DOC_ID);
  const cleanSettings: SiteSettings = {
    siteTitle: settings.siteTitle?.trim() || 'RH Editography',
    siteLogoHeaderUrl: settings.siteLogoHeaderUrl?.trim() || '',
    siteLogoFooterUrl: settings.siteLogoFooterUrl?.trim() || '',
    siteLogoAuthUrl: settings.siteLogoAuthUrl?.trim() || '',
    siteLogoEmailUrl: settings.siteLogoEmailUrl?.trim() || '',
    siteTaglineHeader: settings.siteTaglineHeader?.trim() || 'Digital Template Marketplace',
    siteTaglineFooter: settings.siteTaglineFooter?.trim() || '3D Digital Vector Marketplace',
    siteFaviconUrl: settings.siteFaviconUrl?.trim() || '',
    newsletterEnabled: settings.newsletterEnabled !== undefined ? settings.newsletterEnabled : true,
    newsletterBadgeText: settings.newsletterBadgeText?.trim() || 'Join 35,000+ Designers & Professionals',
    newsletterHeading: settings.newsletterHeading?.trim() || 'Get 20% Off Your First 3D Master Template Download',
    newsletterDescription: settings.newsletterDescription?.trim() || 'Subscribe to the RH Editography VIP briefing for weekly 300 DPI vector releases, security microprint templates, and instant discount passes.',
    newsletterButtonText: settings.newsletterButtonText?.trim() || 'Get VIP Pass',
    newsletterSuccessDiscountCode: settings.newsletterSuccessDiscountCode?.trim() || 'RH3D20',
    newsletterDiscountPercentage: settings.newsletterDiscountPercentage?.trim() || '20%',
    footerDescription: settings.footerDescription?.trim() || 'World-class digital marketplace specialized in editable vector document templates, biometric passports, ID cards, diplomas, financial statements, and corporate agreements.',
    footerCopyright: settings.footerCopyright?.trim() || '© 2026 RH Editography. All Rights Reserved.',
    socialInstagramUrl: settings.socialInstagramUrl?.trim() || '',
    socialWhatsappUrl: settings.socialWhatsappUrl?.trim() || '',
    socialTelegramUrl: settings.socialTelegramUrl?.trim() || '',
    socialDribbbleUrl: settings.socialDribbbleUrl?.trim() || '',
  };

  cachedSiteSettings = cleanSettings;
  try {
    localStorage.setItem('rh_site_settings', JSON.stringify(cleanSettings));
  } catch (e) {}
  applySiteSettingsToDOM(cleanSettings);

  await setDoc(docRef, cleanSettings, { merge: true });
}

export function applySiteSettingsToDOM(settings: SiteSettings): void {
  if (typeof window === 'undefined') return;

  if (settings.siteTitle) {
    const tagline = settings.siteTaglineHeader || 'Digital Template Marketplace';
    document.title = `${settings.siteTitle} • ${tagline}`;
  }

  const iconUrl = settings.siteFaviconUrl?.trim() || "";

  const rels = ['icon', 'shortcut icon', 'apple-touch-icon', 'apple-touch-icon-precomposed'];
  rels.forEach((rel) => {
    let elements = Array.from(document.querySelectorAll(`link[rel="${rel}"]`)) as HTMLLinkElement[];
    if (elements.length === 0) {
      const el = document.createElement('link');
      el.rel = rel;
      el.href = iconUrl;
      document.head.appendChild(el);
    } else {
      elements.forEach((el) => {
        el.href = iconUrl;
      });
    }
  });
}
